import React, { useEffect, useState } from "react";
import type { MediaPreset, MediaView } from "./mediaTypes";
import { deliveryFor, MEDIA_WIDTHS, safeDeliveryUrl } from "./mediaPresets";
import { mediaForUrl } from "./mediaReferences";
import { mediaApi } from "./mediaApi";

type Props = Omit<React.ImgHTMLAttributes<HTMLImageElement>, "src" | "srcSet"> & {
  localPreviewUrl?: string; mediaId?: string; media?: MediaView; preset: MediaPreset; legacyUrl?: string; alt: string;
  /** False on collector screens: the master is never rendered there, only sized copies or the caller's link. */
  allowMaster?: boolean;
};
export const HKPImage = React.forwardRef<HTMLImageElement, Props>(function HKPImage({ localPreviewUrl, mediaId, media: suppliedMedia, preset, legacyUrl, alt, sizes, allowMaster = true, ...props }, ref) {
  const media = suppliedMedia || mediaForUrl(legacyUrl);
  const [resolved, setResolved] = useState<MediaView | undefined>(media);
  const [fallback, setFallback] = useState(false);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let current = true;
    setResolved(media); setFallback(false); setFailed(false);
    if (!media && mediaId) {
      void mediaApi.get(mediaId).then(value => { if (current) setResolved(value); })
        .catch(() => { if (current) setFallback(true); });
    }
    return () => { current = false; };
  }, [mediaId, media, legacyUrl]);
  const selected = deliveryFor(resolved, preset);
  const legacy = safeDeliveryUrl(legacyUrl);
  const preview = localPreviewUrl?.startsWith("blob:") ? localPreviewUrl : undefined;
  // Sized copies first, then the caller's own link; the master is only a last resort for studio uploads
  // whose copies are not made yet, and never wins over a delivered copy link.
  const master = allowMaster ? safeDeliveryUrl(resolved?.masterUrl) : undefined;
  const src = preview || (fallback ? legacy : selected?.url || legacy || master);
  // The preset is the ceiling: a THUMBNAIL never fetches the 800 copy on a 2x screen. The browser still
  // picks a smaller candidate when the caller's `sizes` says the box is small.
  const candidates = selected
    ? resolved?.derivatives.filter(image => image.contentType === selected.contentType
      && image.width <= Math.max(MEDIA_WIDTHS[preset], selected.width)
      && Boolean(safeDeliveryUrl(image.url))) || [] : [];
  const uniqueWidths = Array.from(new Map(candidates.map(image => [image.width, image] as const)).values());
  const srcSet = !fallback && uniqueWidths.length
    ? uniqueWidths.sort((a, b) => a.width - b.width).map(image => image.url + " " + image.width + "w").join(", ")
    : undefined;
  if (!src || failed) return <span role="img" aria-label={alt} className={props.className}>Image unavailable</span>;
  return <img {...props} ref={ref} src={src} srcSet={srcSet}
    sizes={sizes || "(max-width: " + MEDIA_WIDTHS[preset] + "px) 100vw, " + MEDIA_WIDTHS[preset] + "px"}
    alt={alt} loading={props.loading || "lazy"} decoding="async"
    onError={event => {
      if (!fallback && legacy && legacy !== src) setFallback(true);
      else setFailed(true);
      props.onError?.(event);
    }} />;
});
